import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import {
  ClientRegisteredVendor,
  CompanyVendor,
  ExemptVendor,
  Site,
  Vendor
} from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { AssignSitesToVendorModalComponent } from 'src/app/modals/assign-sites-to-vendor-modal/assign-sites-to-vendor-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-client-my-vendor-sites',
  templateUrl: './client-my-vendor-sites.component.html',
  styleUrls: ['./client-my-vendor-sites.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientMyVendorSitesComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private route: ActivatedRoute,
    private Router: Router,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private ModalService: NgbModal,
    private SiteService: SiteService
  ) {}

  reloadGrid: boolean = false;
  VendorId?: number;
  ClientRegisteredVendorId?: number;
  ExemptVendorId?: number;
  Vendor?: Vendor | ExemptVendor | ClientRegisteredVendor;
  CompanyId!: number;

  suppliedGridQueryData?: { [key: string]: any };

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['view', 'delete']
  };

  assignSites() {
    const { componentInstance, result } = this.ModalService.open(
      AssignSitesToVendorModalComponent,
      { backdrop: 'static', size: 'lg' }
    );
    Object.assign(componentInstance, {
      CompanyId: this.CompanyId,
      VendorId: this.VendorId,
      ExemptVendorId: this.ExemptVendorId,
      ClientRegisteredVendorId: this.ClientRegisteredVendorId
    });

    from(result).subscribe({
      next: () => {
        this.reloadGrid = true;
        this._cdr.markForCheck();
      }
    });
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    const { id: SiteId } = rowData as Site;

    if (!SiteId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot navigate to site. No site ID found.',
        { returnError: true }
      );
    }

    if (action === 'view') {
      this.Router.navigate([
        'client',
        this.AuthService.getUserCompanyId(),
        'site',
        SiteId,
        'siteDetails'
      ]);
    }

    if (action === 'delete') {
      this.SiteService.deleteSiteVendor({
        SiteId,
        VendorId: this.VendorId || null,
        ExemptVendorId: this.ExemptVendorId || null,
        ClientRegisteredVendorId: this.ClientRegisteredVendorId || null
      }).subscribe({
        next: () => {
          this.reloadGrid = true;
          this.ToastService.addSuccessToast(
            'Success',
            'Successfully removed Vendor from site.'
          );
          this._cdr.markForCheck();
        }
      });
    }
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const VendorId = Number(paramMap.get('VendorId'));
        const ExemptVendorId = Number(paramMap.get('ExemptVendorId'));
        const ClientRegisteredVendorId = Number(
          paramMap.get('ClientRegisteredVendorId')
        );
        const CompanyId = Number(paramMap.get('CompanyId'));

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
            returnError: true
          });
        }

        const numberOfVendorIds = [
          VendorId,
          ExemptVendorId,
          ClientRegisteredVendorId
        ].reduce((accum, value) => {
          if (value) {
            return accum + 1;
          }

          return accum;
        }, 0);

        if (numberOfVendorIds === 0) {
          throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
            returnError: true
          });
        }

        if (numberOfVendorIds > 1) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Multiple Vendor Ids found.',
            { returnError: true }
          );
        }

        Object.assign(this, {
          VendorId: VendorId || undefined,
          ExemptVendorId: ExemptVendorId || undefined,
          ClientRegisteredVendorId: ClientRegisteredVendorId || undefined,
          CompanyId
        });

        const companyVendorQueryObj = {};
        const vendorQuery = {
          attributes: ['legalName']
        };

        if (VendorId) {
          Object.assign(companyVendorQueryObj, {
            VendorId,
            Vendor: JSON.stringify(vendorQuery)
          });

          this.suppliedGridQueryData = {
            SiteVendor: {
              VendorId,
              options: { required: true }
            }
          };
        }

        if (ExemptVendorId) {
          Object.assign(companyVendorQueryObj, {
            ExemptVendorId,
            ExemptVendor: JSON.stringify(vendorQuery)
          });

          this.suppliedGridQueryData = {
            SiteVendor: {
              ExemptVendorId,
              options: { required: true }
            }
          };
        }

        if (ClientRegisteredVendorId) {
          Object.assign(companyVendorQueryObj, {
            ClientRegisteredVendorId,
            ClientRegisteredVendor: JSON.stringify(vendorQuery)
          });

          this.suppliedGridQueryData = {
            SiteVendor: {
              ClientRegisteredVendorId,
              options: { required: true }
            }
          };
        }

        this.CompanyVendorService.getCompanyVendor(
          companyVendorQueryObj
        ).subscribe({
          next: (companyVendor: CompanyVendor) => {
            const { Vendor, ClientRegisteredVendor, ExemptVendor } =
              companyVendor;

            if (Vendor) {
              this.Vendor = Vendor;
            }

            if (ExemptVendor) {
              this.Vendor = ExemptVendor;
            }

            if (ClientRegisteredVendor) {
              this.Vendor = ClientRegisteredVendor;
            }
            this._cdr.markForCheck();
          }
        });
        this._cdr.markForCheck();
      }
    });
  }
}
